import React, { useState } from "react";
import { PlusOne } from "../types";

interface PlusOneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (plusOnes: PlusOne[]) => void;
}

const PlusOneModal: React.FC<PlusOneModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [plusOnes, setPlusOnes] = useState<PlusOne[]>([
    { name: "", phone: "" },
  ]);
  const [errors, setErrors] = useState<string[]>([]);

  const addPlusOne = () => {
    setPlusOnes([...plusOnes, { name: "", phone: "" }]);
  };

  const removePlusOne = (index: number) => {
    if (plusOnes.length > 1) {
      setPlusOnes(plusOnes.filter((_, i) => i !== index));
    }
  };

  const updatePlusOne = (
    index: number,
    field: keyof PlusOne,
    value: string
  ) => {
    const updated = [...plusOnes];
    updated[index][field] = value;
    setPlusOnes(updated);
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    plusOnes.forEach((plusOne, index) => {
      if (!plusOne.name.trim()) {
        newErrors.push(`Plus one #${index + 1} name is required`);
      }
      if (!plusOne.phone.trim()) {
        newErrors.push(`Plus one #${index + 1} phone number is required`);
      } else if (!/^[0-9]{10}$/.test(plusOne.phone.replace(/\D/g, ""))) {
        newErrors.push(`Plus one #${index + 1} phone number must be 10 digits`);
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      // Clean phone numbers (remove non-digits)
      const cleanedPlusOnes = plusOnes.map((plusOne) => ({
        ...plusOne,
        phone: plusOne.phone.replace(/\D/g, ""),
      }));
      onConfirm(cleanedPlusOnes);
      setPlusOnes([{ name: "", phone: "" }]);
      setErrors([]);
    }
  };

  const handleClose = () => {
    setPlusOnes([{ name: "", phone: "" }]);
    setErrors([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="plus-one-modal">
        {/* Header */}
        <div className="plus-one-header">
          <div className="plus-one-title">
            <div className="plus-one-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="9"
                  cy="7"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3>Add Your Guests</h3>
          </div>
          <button className="plus-one-close" onClick={handleClose}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="18"
                y1="6"
                x2="6"
                y2="18"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="6"
                y1="6"
                x2="18"
                y2="18"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="plus-one-body">
          <p className="plus-one-description">
            Add the names and phone numbers of people you'd like to bring as
            guests. The host will receive a request to add them to their member
            list.
          </p>

          {errors.length > 0 && (
            <div className="plus-one-errors">
              {errors.map((error, index) => (
                <div key={index} className="plus-one-error">
                  <svg className="error-icon" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <line
                      x1="15"
                      y1="9"
                      x2="9"
                      y2="15"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <line
                      x1="9"
                      y1="9"
                      x2="15"
                      y2="15"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  {error}
                </div>
              ))}
            </div>
          )}

          <div className="plus-one-forms">
            {plusOnes.map((plusOne, index) => (
              <div key={index} className="plus-one-form">
                <div className="plus-one-form-header">
                  <div className="plus-one-form-title">
                    <span className="guest-number">#{index + 1}</span>
                    <span className="guest-label">Guest</span>
                  </div>
                  {plusOnes.length > 1 && (
                    <button
                      type="button"
                      className="remove-guest-btn"
                      onClick={() => removePlusOne(index)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="plus-one-inputs">
                  <div className="plus-one-input-group">
                    <label className="plus-one-label">
                      <svg
                        className="input-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="7"
                          r="4"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="plus-one-input"
                      value={plusOne.name}
                      onChange={(e) =>
                        updatePlusOne(index, "name", e.target.value)
                      }
                      placeholder="Enter guest's full name"
                    />
                  </div>

                  <div className="plus-one-input-group">
                    <label className="plus-one-label">
                      <svg
                        className="input-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="plus-one-input"
                      value={plusOne.phone}
                      onChange={(e) =>
                        updatePlusOne(index, "phone", e.target.value)
                      }
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button type="button" className="add-guest-btn" onClick={addPlusOne}>
            <svg
              className="add-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="12"
                y1="5"
                x2="12"
                y2="19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add Another Guest
          </button>
        </div>

        {/* Footer */}
        <div className="plus-one-footer">
          <button className="plus-one-cancel-btn" onClick={handleClose}>
            Cancel
          </button>
          <button className="plus-one-submit-btn" onClick={handleConfirm}>
            <svg
              className="submit-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Send Request
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlusOneModal;
